/**
 * Reveals the How it works timeline as it scrolls into view — one sweep, the
 * first time it enters the viewport.
 *
 * Plain HTMLElement rather than Horizon's Component base, matching
 * solution-spotlight.js and reviews-wall.js: this needs no shadow DOM, refs or
 * declarative event binding, just an IntersectionObserver.
 *
 * All the motion lives in the section's stylesheet. This file only flips
 * `data-reveal` to "in", and sets --how-it-works-step-count so the rail's sweep
 * duration can span however many steps the merchant actually added. It never
 * touches opacity or transform directly, so the timing, distance, stagger and
 * reduced-motion handling stay in one place in CSS.
 *
 * Why the hidden starting state is keyed off `:defined` in that stylesheet and
 * not applied here: if this file fails to load, the custom element is never
 * upgraded, `:not(:defined)` still matches, and the whole timeline renders
 * normally. Hiding it from JS would instead mean a broken script leaves the
 * section permanently blank.
 */
class HowItWorksTimeline extends HTMLElement {
  connectedCallback() {
    // The rail's sweep has to finish with the last column, so its duration
    // depends on how many steps there are. Read from the DOM rather than the
    // schema so it stays correct when blocks are added or removed.
    const steps = this.querySelectorAll('.how-it-works__steps > *').length;
    if (steps > 0) {
      this.style.setProperty('--how-it-works-step-count', String(steps));
    }

    // No IntersectionObserver (or re-connected after already revealing): show it
    // outright rather than leaving it hidden.
    if (!('IntersectionObserver' in window) || this.dataset.reveal === 'in') {
      this.dataset.reveal = 'in';
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          this.dataset.reveal = 'in';
          // One-shot: this is a reveal, not a scrubbed effect, so it must not
          // replay or reverse when the section is scrolled back past.
          this.disconnectObserver();
        }
      },
      {
        // Start once the timeline is properly on screen rather than the instant
        // its first pixel appears, so the sweep is actually watched.
        rootMargin: '0px 0px -15% 0px',
        threshold: 0,
      }
    );

    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.disconnectObserver();
  }

  disconnectObserver() {
    this.observer?.disconnect();
    this.observer = null;
  }
}

if (!customElements.get('how-it-works-timeline')) {
  customElements.define('how-it-works-timeline', HowItWorksTimeline);
}
